import { DatePicker, Form, Input, message, Select, Tooltip, Checkbox } from "antd";
import { Rule } from "antd/lib/form";
import { PathDirectorySelect } from "Components/Form/PathDirectorySelect";
import { Button } from "Components/General/Button";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { I18N_NS } from "Config/i18n";
import { TFunction } from "i18next";
import { dayjs } from "Config/dayjs";
import { metadataStore } from "Pages/Admin/Stores/MetadataStore";
import { groupListStore } from "Pages/Project/Groups/Stores/GroupsStore";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { contactsStore } from "Stores/ContactsStore";
import { formatUsername } from "utils/formatUsername";

interface CreateNewObjectTypeModalProps extends ModalComponentProps {
  createNewAttributes: (
    tableId: number | string,
    referenceId: number,
    body,
    isSubObject?: boolean,
    currentTableId?: number,
    fromSearch?: boolean
  ) => Promise<void>;
  currentTableId?: number;
  tableId: number;
  referenceId?: number;
  attributes?: API_ValueTableAttribute[];
  isSubObject?: boolean;
  fromSearch?: boolean;
}

export const getAttributeFormItem = (
  attribute: API_ValueTableAttribute,
  index: number,
  t: TFunction,
  initialValueBuffer: {},
  initialValue?: string,
  disabled?: boolean
) => {
  const getRules = () => {
    if (attribute.datatype === "BOOLEAN") {
      return [];
    }
    const rules: Rule[] = [];
    if (attribute.mandatory && attribute.datatype !== "AUTOINC") {
      rules.push({ required: true, message: t("please input {{name}}", { name: attribute.name }) });
    }
    switch (attribute.datatype) {
      case "INTEGER":
        rules.push({ pattern: new RegExp(/^[0-9]+$/), message: t("Please use an integer") });
        break;
      case "DOUBLE":
        rules.push({
          pattern: new RegExp(/^[0-9]+(\.[0-9]{1,2})?$/),
          message: t("Please use a number with max. 2 decimals"),
        });
        break;
      case "FLOAT":
        rules.push({ pattern: new RegExp(/^[0-9]+(\.[0-9]+)?$/), message: t("Please use a floating number") });
        break;
    }
    return rules;
  };

  const getEditItem = () => {
    switch (attribute.datatype) {
      case "BOOLEAN":
        return <Checkbox disabled={disabled} />;
      case "LIST":
        return (
          <Select
            allowClear={!attribute.mandatory}
            disabled={disabled}
            options={attribute.additional_parameters.map((item) => ({
              key: item,
              value: item,
              label: item,
            }))}
          />
        );
      case "SYSUSER":
        return (
          <Select
            allowClear={!attribute.mandatory}
            disabled={disabled}
            options={contactsStore.projectUsers
              .filter((user) => user.active)
              .map((user) => ({
                key: "" + user.id,
                value: "" + user.id,
                label: formatUsername({ firstName: user.firstName, surname: user.surname }),
              }))}
          />
        );
      case "SYSGROUP":
        return (
          <Select
            allowClear={!attribute.mandatory}
            disabled={disabled}
            options={groupListStore.publicGroupsUnfiltered.map((group) => ({
              key: "" + group.id,
              value: "" + group.id,
              label: group.name,
            }))}
          />
        );
      case "FOLDER":
        return <PathDirectorySelect />;
      case "METADATA":
        return (
          <Select
            allowClear={!attribute.mandatory}
            disabled={disabled}
            options={metadataStore.getMetaById(+attribute.additional_parameters[0])?.rangeList.map((item, idx) => ({
              key: item.value + idx,
              value: item.value,
              label: `${item.value} - ${item?.description}`,
            }))}
          />
        );
      case "DATE":
        return <DatePicker format="DD.MM.YYYY" disabled={disabled} />;
      case "TEXTBOX":
        return <Input.TextArea disabled={disabled} rows={6} />;
      case "AUTOINC":
        return <Input disabled={true} />;
      default:
        return <Input disabled={disabled} />;
    }
  };

  const getInitialValue = () => {
    switch (attribute.datatype) {
      case "BOOLEAN":
        return initialValue === undefined ? false : initialValue;
      case "DATE":
        return initialValue ? dayjs(initialValue, "DD.MM.YYYY") : undefined;

      default:
        return initialValue;
    }
  };

  initialValueBuffer[attribute.id] = getInitialValue();
  return (
    <Form.Item
      key={"outer-" + attribute.id}
      label={<Tooltip title={attribute.description}>{attribute.name}</Tooltip>}
      labelCol={{ span: 8 }}
      style={{ marginRight: 25 }}
    >
      <div className="flex" style={{ alignItems: "center" }}>
        <div style={{ flexGrow: 1 }}>
          <Form.Item
            noStyle
            name={attribute.id}
            key={attribute.id}
            rules={getRules()}
            valuePropName={attribute.datatype !== "BOOLEAN" ? "value" : "checked"}
          >
            {getEditItem()}
          </Form.Item>
        </div>
        {attribute.unit && <span style={{ flexShrink: 0, marginLeft: 10 }}> {attribute.unit}</span>}
      </div>
    </Form.Item>
  );
};

export const CreateNewObjectTypeModal = ({
  createNewAttributes,
  currentTableId,
  tableId,
  referenceId,
  attributes,
  isSubObject,
  fromSearch,
  hideModal,
}: CreateNewObjectTypeModalProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [loading, setLoading] = useState(false);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      await createNewAttributes(tableId, referenceId, values, isSubObject, currentTableId, fromSearch);
      hideModal();
      message.success(t("Success"));
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const initalValues = {};
  const dataList = attributes?.map((item, index) => {
    return getAttributeFormItem(item, index, t, initalValues);
  });

  return (
    <Form className="ant-form-item-with-postfix" onFinish={onFinish}>
      <div style={{ overflowY: "auto", maxHeight: 400 }}>{dataList}</div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
        <Button style={{ marginRight: 10 }} onClick={hideModal}>
          {t("Cancel")}
        </Button>
        <Button htmlType="submit" type="primary" loading={loading}>
          {t("Save")}
        </Button>
      </div>
    </Form>
  );
};
